import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const settings = {
    language: {
        options: {
            system: 'System',
            'pt-BR': 'Português (BR)',
            en: 'English',
        },
    },
    // Stack header titles (app/settings/_layout.tsx) — includes screens from
    // other areas because the layout is a single file.
    screens: {
        index: 'Settings',
        preferences: 'Preferences',
        appearance: 'Appearance',
        appIcon: 'App icon',
        homeLayout: 'Customize home',
        analysisLayout: 'Customize analysis',
        tabSlot: 'Bar shortcut',
        homeShortcuts: 'Home screen shortcuts',
        reminders: 'Reminders',
        iosQuickCapture: 'Quick capture',
        ai: 'AI assistant',
        maintenance: 'Maintenance',
        backup: 'Backup & Sync',
        backups: 'Restore points',
        backupMore: 'More options',
        import: 'Import & export',
        security: 'Security',
        autoCategorization: 'Automatic categorization',
        feedback: 'Send feedback',
    },
    index: {
        sections: {
            personalization: 'Personalization',
            notifications: 'Notifications',
            automation: 'Automation',
            dataSecurity: 'Data and security',
        },
        rows: {
            appearance: 'Appearance',
            homeLayout: 'Customize home',
            analysisLayout: 'Customize analysis',
            tabSlot: 'Bar shortcut',
            preferences: 'Preferences',
            reminders: 'Reminders',
            remindersDisabled: 'Off',
            bankNotifications: 'Bank notifications',
            bankNotificationsValue: 'Android',
            bankNotificationsOff: 'Access off',
            bankNotificationsSilent: 'Reading stalled',
            iosQuickCapture: 'Quick capture',
            iosQuickCaptureValue: 'iPhone',
            autoCategorization: 'Automatic categorization',
            aiAssistant: 'AI assistant',
            aiPending: 'Pending',
            security: 'Security',
            securityEnabled: 'On',
            securityDisabled: 'Off',
            backup: 'Backup',
            backupValue: 'Cloud and files',
            dataTransfer: 'Import & export',
            maintenance: 'Maintenance',
            managePlan: 'Manage plan',
            managePlanValue: 'Pro',
            meetPro: 'Meet Kontto Pro',
            rateApp: 'Rate Kontto',
            sendFeedback: 'Send feedback',
        },
    },
    iosQuickCapture: {
        hero: {
            eyebrow: 'IPHONE SHORTCUTS',
            title: 'Capture without opening Kontto',
            subtitle: 'With Apple Pay or a double tap. Review everything in Pending.',
            subtitleBank: 'From your bank notifications, Apple Pay or a double tap. Review everything in Pending.',
        },
        privacy: 'Your captures stay on your iPhone.',
        triggersLabel: 'Ways to capture',
        manualSetupTitle: 'Choose the gesture in iPhone Settings',
        manualSetupNote: 'The shortcut above comes ready to use — nothing to build. Just turn on the gesture:',
        bankSetup: {
            note: 'When your bank notifies you of a Pix or purchase, Kontto saves it as pending. Works with 40+ banks and the iPhone Wallet.',
            steps: [
                'Tap Add to Shortcuts and confirm on your iPhone.',
                'In the Shortcuts app, go to Library › Automation and turn on “When I get a notification from any of… apps”. It arrives turned off.',
                'Done. Your bank’s next notification goes to Pending.',
            ],
            done: 'I turned the automation on',
        },
        walletGuide: {
            once: 'Set up once in Shortcuts',
            inShortcuts: 'IN SHORTCUTS',
            example: 'EXAMPLE IN KONTTO',
            progress: 'Step {{current}} of {{total}}',
            next: 'Next step',
            back: 'Back',
            finish: 'I saved it in Shortcuts',
            returnHint: 'Switch between Shortcuts and Kontto to follow along. The guide stays on this step.',
            // Replicas of the Shortcuts app — same labels the user will see there.
            automationTab: 'Automation',
            noAutomations: 'No Automations',
            newAutomation: 'New Automation',
            personalAutomation: 'Personal Automation',
            transactionTrigger: 'Transaction',
            transactionExample: 'E.g. “When I tap a Card or Pass in Wallet”',
            walletTrigger: 'Wallet',
            navBack: 'Back',
            navNext: 'Next',
            navDone: 'Done',
            whenITap: 'When I Tap',
            cardsHeader: 'Card',
            creditCard: 'Credit card',
            debitCard: 'Debit card',
            runAfterConfirmation: 'Run After Confirmation',
            runImmediately: 'Run Immediately',
            notifyWhenRun: 'Notify When Run',
            blankAutomation: 'New Blank Automation',
            blankShortcut: 'New Blank Shortcut',
            searchActions: 'Search Actions',
            // Kontto's own action and its fields have no translation in iOS, so Shortcuts shows
            // them in Portuguese on any iPhone language. The guide repeats them as they appear.
            captureAction: 'Capturar compra da Carteira',
            captureActionInline: 'Capturar compra de',
            alwaysAsk: 'Always Ask',
            shortcutInput: 'Shortcut Input',
            amountField: 'Valor',
            amountProperty: 'Amount',
            ifAsked: 'Choose Amount from the list. With the whole Shortcut Input, the purchase doesn’t reach Kontto.',
            merchantField: 'Estabelecimento',
            cardField: 'Cartão ou passe',
            screens: {
                create: 'Shortcuts replica: Automation tab, New Automation button and the {{trigger}} option.',
                options: 'Shortcuts replica: cards checked, Run Immediately selected and Notify When Run off.',
                action: 'Shortcuts replica: {{blankTile}}, a search for Kontto and the Capturar compra da Carteira action.',
                connect: 'Shortcuts replica: the action’s amount field, the variable bar with Shortcut Input, the Amount property chosen, and the filled action.',
                save: 'Shortcuts replica: Done button in the top right corner.',
            },
            examplePurchase: 'Apple Pay purchase',
            reviewFirst: 'It only affects your balance after you confirm.',
            steps: [
                {
                    title: 'Create the automation',
                    body: 'In Shortcuts, open the Automation tab, tap New Automation and choose {{trigger}}.',
                    hint: 'Already have automations? The button becomes a + in the top right corner.',
                },
                {
                    title: 'Choose your cards',
                    body: 'Check the cards you use with Apple Pay and choose Run Immediately. Leave Notify When Run off.',
                    hint: 'Tap Next to continue.',
                },
                {
                    title: 'Add the Kontto action',
                    body: 'Tap {{blankTile}}. In Search Actions, type Kontto and choose the action below. Its name appears in Portuguese.',
                    hint: 'Apple Pay does not need a separate shortcut.',
                },
                {
                    title: 'Connect the purchase amount',
                    body: 'Tap Valor in the action. In the bar above the keyboard, tap Shortcut Input. Then tap Shortcut Input inside the action and choose Amount.',
                    hint: 'Already have the automation? Check that the field shows Amount, not Shortcut Input. Estabelecimento and Cartão ou passe are optional.',
                },
                {
                    title: 'Save and you’re ready',
                    body: 'Tap Done in the top right corner. Your next contactless purchase will go to Pending.',
                    hint: 'The status turns Active when the first real purchase arrives.',
                },
            ],
        },
        howItWorks: {
            exampleOnly: 'Interactive demo · does not save data',
            transactionSaved: 'Transaction saved',
            removedFromPending: 'The expense leaves Pending and appears in your transactions.',
            tapToReview: 'Tap the expense to review it',
            quickCapture: 'Quick capture',
            exampleAccount: 'Example account',
            exampleCard: 'Example card',
            exampleCategory: 'Food',
            sceneLabels: ['In the queue', 'Review', 'Result'],
            restart: 'Try again',
            discardedTitle: 'Capture discarded',
            discardedExample: 'The capture leaves the queue without creating a transaction.',
            open: 'See how it works',
            title: 'How it works',
            close: 'Close',
            saved: 'Capture saved',
            now: 'now',
            pendingHeader: 'Pending · 1',
            confirm: 'Confirm',
            discard: 'Discard',
            ask: 'How much was it?',
            chips: ['Food', 'Transport', 'Other'],
            cases: {
                ios_wallet: {
                    label: 'Apple Pay',
                    amount: 'R$ 48,90',
                    merchant: 'Padaria Estrela',
                    category: 'Food · Nubank · today 14:02',
                    captions: [
                        'After setup, tap to pay. Your purchase appears in Pending like this.',
                        'Tap the purchase, review the card, category and amount, then confirm.',
                        'Done: the purchase has left the queue and become a transaction.',
                    ],
                },
                ios_quick_manual: {
                    label: 'Double tap',
                    amount: 'R$ 35,00',
                    merchant: 'Quick expense',
                    category: 'Food · today 12:15',
                    captions: [
                        'Double-tap the back of your iPhone and enter an amount and category. The expense appears here.',
                        'Tap the expense and review its account and category before confirming.',
                        'Done: the expense has left the queue and entered your transactions.',
                    ],
                },
            },
        },
        shortcut: {
            title: 'Double-tap shortcut',
            ready: 'Install once to open the form with the gesture.',
            verifying: 'Checking installation…',
            installed: 'Installed · version {{version}}',
            notFound: 'We could not find the shortcut on your iPhone',
            outdated: 'New version available',
            updateHint: 'In Shortcuts, choose Replace. “Keep Both” creates a copy and does not update this shortcut.',
        },
        trigger: {
            configure: 'Set up',
            waiting: 'Waiting',
            active: 'Active',
            ios_wallet: {
                title: 'Apple Pay',
                subtitle: 'Automatic when you tap to pay',
                subtitleSecondary: "For a card whose bank doesn't notify you",
            },
            ios_quick_manual: {
                title: 'Double tap',
                subtitle: 'Tap the back and enter an amount',
                steps: [
                    'In Settings › Accessibility › Touch › Back Tap, choose Double Tap.',
                    'Select the “Capturar No Kontto” shortcut.',
                    'Double-tap the back of your iPhone to enter an expense.',
                ],
            },
            ios_bank_notification: {
                title: 'Bank apps',
                subtitle: 'Pix and purchases from your bank’s notification',
            },
        },
        actions: {
            addShortcut: 'Add to Shortcuts',
            addShortcutAgain: 'Add again',
            updateShortcut: 'Update shortcut',
            retryVerifyUpdated: 'Already updated, check again',
            retryVerify: 'Already installed, try again',
            markConfigured: 'I set it up',
            manage: 'Manage',
            openShortcuts: 'Open Shortcuts',
            viewPending: 'Pending',
        },
        notes: {
            installFirst: 'Install the shortcut for Double Tap. Apple Pay can be set up separately.',
            shortcutNeeded: 'Double Tap needs the shortcut above. Apple Pay works separately.',
            waitingBecomesActive: 'The status turns Active after the first real capture.',
            reviewPending: 'Review before it affects your balance',
            systemSetupTitle: 'Why is setup required?',
            systemSetupBody: 'iOS requires you to authorize the Wallet automation and choose the gesture. Kontto provides the actions but cannot enable those settings for you.',
            walletSetupBody: 'For security, iOS only lets you create this automation yourself. Set it up once, then each purchase runs automatically.',
            simulator: 'Apple Pay and Double Tap do not trigger in Simulator. The test above validates the same inbox used by the real actions.',
        },
        toasts: {
            setupFailed: 'Could not save. Please try again.',
            shortcutFailed: 'Could not open Shortcuts',
        },
    },
    appearance: {
        sections: {
            theme: 'Theme',
            accentColor: 'Accent color',
            addButton: 'Add button',
        },
        coloredFab: {
            title: 'Colored + button',
            description: "Paints the add button with the theme's accent color.",
            proBadge: 'PRO',
        },
        save: {
            getPro: 'Get Pro',
            saveTheme: 'Save theme',
            success: 'Theme saved!',
        },
    },
    security: {
        status: {
            onTitle: 'Kontto protected',
            onSubtitle: 'Opens with {{methods}} · locks after {{minutes}} min',
            methodPin: 'PIN',
            methodPinBiometric: 'PIN or {{biometric}}',
            offTitle: 'Kontto unlocked',
            offBadge: 'Off',
            offBody: 'Without a PIN, anyone holding your unlocked phone can open Kontto and see your accounts, cards and transactions.',
        },
        groups: {
            access: 'How to unlock',
            autoLock: 'Auto-lock',
        },
        actions: {
            createPin: 'Create PIN',
            changePin: 'Change PIN',
            removePin: 'Remove PIN',
            cancel: 'Cancel',
        },
        biometric: {
            title: 'Unlock with {{biometric}}',
            description: 'This device only — the PIN still works as a fallback.',
        },
        changePin: {
            description: 'Confirm your current PIN before choosing a new one.',
        },
        removePin: {
            description: 'The app goes back to opening without asking for anything.',
        },
        autoLock: {
            minutesOption: '{{minutes}} min',
            title: 'Ask for the PIN again',
            subtitle: 'Time with the app closed or in the background before Kontto locks itself.',
            hint: 'Come back before {{minutes}} min and the app opens straight away.',
        },
        lockOnColdStart: {
            title: 'Always ask for the PIN on open',
            description: 'Once the app is closed, by you or by the system, it asks for the PIN on open — even before the time above. Just switching apps and coming back follows the time.',
        },
        footerNote: "The PIN is stored on this device only, in the system keystore — Kontto keeps no copy and cannot recover it. Keep a backup of your data.",
        flows: {
            setup: { title: 'Create PIN', subtitle: 'Enter a 4-digit PIN' },
            setupConfirm: { title: 'Confirm PIN', subtitle: 'Enter the same PIN again' },
            changeVerify: { title: 'Current PIN', subtitle: 'Confirm your PIN to continue' },
            changeNew: { title: 'New PIN', subtitle: 'Enter the new 4-digit PIN' },
            changeConfirm: { title: 'Confirm new PIN', subtitle: 'Enter the new PIN again' },
            removeVerify: { title: 'Remove PIN', subtitle: 'Confirm your PIN to remove it' },
            biometricVerify: { title: 'Confirm PIN', subtitle: 'Confirm your PIN to change biometrics' },
        },
        toasts: {
            pinCreated: 'PIN created',
            pinChanged: 'PIN changed',
            pinRemoved: 'PIN removed',
            biometricEnabled: 'Biometrics enabled',
            biometricEnabledDescription: 'Biometrics apply to this device only.',
            biometricDisabled: 'Biometrics disabled',
            biometricDisabledDescription: "Unlocking again relies only on this device's PIN.",
        },
        errors: {
            pinMismatch: "PINs don't match",
            pinIncorrect: 'Incorrect PIN',
            validationFailed: 'Failed to validate the PIN',
        },
    },
    preferences: {
        hub: {
            searchPlaceholder: 'Search settings',
            noResults: 'No setting matches “{{query}}”.',
            changedBanner: {
                prefix: 'You changed ',
                count_one: '{{count}} setting',
                count_other: '{{count}} settings',
                suffix: " from Kontto's defaults.",
                action: 'View',
            },
            summary: {
                and: ' and ',
                andMore: ' and {{count}} more',
                allOff: 'All off',
            },
        },
        groups: {
            statement: 'Transactions',
            balance: 'Balance and Home',
            analysis: 'Analysis and reports',
            entries: 'Entries and shortcuts',
            haptics: 'Haptics',
            region: 'Language, currency and dates',
        },
        summary: {
            categoryLabel: {
                category: 'category',
                subcategory: 'subcategory',
                both: 'category › subcategory',
            },
            showTagsInStatement: 'tags',
            showNotesInStatement: 'notes',
            showTransactionTime: 'time',
            showPeriodBalance: 'period balance',
            showDailyRunningBalance: 'daily balance',
            pendingOnTop: 'pending highlighted',
            overdueInCurrentMonth: 'overdue in the current month',
            showReservaMovementsInStatement: 'pockets',
            separateCardPurchases: 'separate card purchases',
            onlyConfirmedTransactions: 'cleared only',
            includeReceivablesInBalance: '“owed to me” in the balance',
            standaloneMonthlyForecast: 'projected balance per month',
            monthResultInCurrentMonth: 'month result in the current month',
            monthStartDay: 'month starts on day {{day}}',
            showAccountReserved: 'reserved on accounts',
            installmentsMonthly: 'installments month by month',
            installmentsFullValue: 'installments at full value',
            invoicesByDueMonth: 'bill in its due month',
            invoicesByClosingMonth: 'bill in its closing month',
            pocketQuickAction: 'pocket on “+”',
            numericKeypad: 'number pad',
            calculatorKeypad: 'calculator keypad',
            hapticsOff: 'off',
            hapticsExceptTabs: 'on, except when switching tabs',
            hapticsWithTabs: 'on, including when switching tabs',
        },
        rows: {
            statementCategoryLabel: { title: 'Category on transactions' },
            showTagsInStatement: { title: 'Tags' },
            showNotesInStatement: { title: 'Notes' },
            showTransactionTime: { title: 'Time', hint: 'Also enables the time field when adding an entry.' },
            showPeriodBalance: { title: 'Period balance', hint: 'Summary at the top and the result of each day.' },
            showDailyRunningBalance: { title: 'Daily running balance', hint: 'Hidden while searching or filtering.' },
            runningBalanceIncludesOpening: { title: 'Include previous balance' },
            pendingOnTop: { title: 'Pending highlighted' },
            overdueInCurrentMonth: {
                title: 'Overdue in the current month',
                hint: 'Pending items from past months at the top of Transactions and in To pay and To receive.',
            },
            showReservaMovementsInStatement: {
                title: 'Pockets in the statement',
                hint: 'Saving and withdrawing show up as a gray line. They do not change totals.',
            },
            separateCardPurchases: {
                title: 'Separate card purchases',
                hint: 'Lists each purchase instead of grouping it into the bill.',
            },
            onlyConfirmedTransactions: { title: 'Cleared only', hint: 'Hides pending and projected items.' },
            monthStartDay: {
                title: 'Month start',
                hint: 'For people who budget from paycheck to paycheck. Screens show the period instead, like “Oct 20 – Nov 19”.',
                valueCalendar: 'Day 1 · calendar',
                valueDay: 'Day {{day}}',
                sheetTitle: 'Month start',
                sheetHint: 'Each month runs from the chosen day to the day before it in the next month. In a shorter month it starts on the last day. Applies to all history: past months’ balances are recomputed.',
                applyToBudgets: {
                    title: 'Apply to budgets?',
                    message_one: '{{count}} monthly budget starts on day {{previousDay}}. Make it start on day {{day}} too?',
                    message_other: '{{count}} monthly budgets start on day {{previousDay}}. Make them start on day {{day}} too?',
                    apply: 'Apply',
                    skip: 'Not now',
                },
            },
            includeReceivablesInBalance: {
                title: 'Include “owed to me” in the balance',
                hint: 'Projections still use cash only.',
            },
            standaloneMonthlyForecast: { title: 'Projected balance per month', hint: 'Applies to future months.' },
            monthResultInCurrentMonth: {
                title: 'Also in the current month',
                hint: 'The top shows income minus expenses instead of the balance in the account.',
            },
            showAccountReserved: { title: 'Reserved on accounts' },
            installmentsFullValue: {
                title: 'Installment plans at full value',
                hint: 'The bill and statement still show each installment.',
            },
            invoicesByClosingMonth: {
                title: 'Card bill in its closing month',
                hint: 'For cards that close at the end of the month and are due the following month.',
            },
            calculatorKeypad: {
                title: 'Calculator keypad',
                hint: 'When off, the calculator is still one tap away on the icon.',
            },
            showPocketQuickAction: { title: 'Pocket on the “+” button' },
            hapticsEnabled: { title: 'In-app haptics' },
            tabHapticsEnabled: { title: 'Haptics when switching tabs' },
            language: { title: 'Language' },
            currency: { title: 'Currency', hint: 'Only changes the symbol — amounts are not converted.' },
            dateFormat: { title: 'Date format' },
        },
        changed: {
            title: 'Not default',
            subtitle_one: '{{count}} setting differs from Kontto',
            subtitle_other: '{{count}} settings differ from Kontto',
            on: 'On',
            off: 'Off',
            day: 'Day {{day}}',
            restore: 'Restore',
            restoreAccessibility: 'Restore the default for {{title}}',
            othersNote_one: 'The other setting is as Kontto ships it. Language, currency and date format are not listed here.',
            othersNote_other: 'The other {{count}} settings are as Kontto ships them. Language, currency and date format are not listed here.',
            empty: 'Everything is as Kontto ships it.',
            emptyNote: 'Language, currency and date format are not listed here.',
            restoreAll: 'Restore all defaults',
            confirm: {
                title: 'Restore defaults?',
                message: 'The settings in this list go back to how Kontto ships them. Language, currency and date format stay as they are.',
                confirm: 'Restore',
            },
        },
        preview: {
            label: 'Preview',
            statement: {
                title: 'In Transactions',
                food: 'Food',
                delivery: 'Delivery',
                transport: 'Transport',
                rideApp: 'Ride app',
                power: 'Power bill',
                internet: 'Internet',
                housing: 'Housing',
                utilities: 'Utilities',
                tag: 'lunch',
                note: 'split with Ana',
                reserva: 'Emergency fund',
            },
            balance: {
                title: 'On Home',
                thisMonth: 'This month',
                nextMonth: 'Next month',
            },
            analysis: {
                title: 'Expenses per month',
                selected: 'August',
                months: {
                    m1: 'Jun',
                    m2: 'Jul',
                    m3: 'Aug',
                    m4: 'Sep',
                },
            },
        },
        statement: {
            categoryLabel: {
                title: 'Category on transactions',
                description: 'What shows under each transaction name in the list. Without a subcategory, it shows the category.',
                options: {
                    category: 'Category',
                    subcategory: 'Subcategory',
                    both: 'Both',
                },
            },
            tags: {
                title: 'Tags on transactions',
                description: 'Shows the tags for each transaction right in the list.',
            },
            notes: {
                title: 'Notes on transactions',
                description: 'Shows the note for each transaction right in the list.',
            },
            time: {
                title: 'Time on transactions',
                description: 'Shows the time field when creating/editing and the time in lists. Entries for today without a chosen time record the current time.',
            },
            separateCardPurchases: {
                title: 'Separate card purchases',
                description: 'Lists each card purchase instead of grouping it into the statement.',
            },
            onlyConfirmed: {
                title: 'Cleared only',
                description: 'Hides pending and projected items; card appears only once the statement is paid.',
            },
            periodBalance: {
                title: 'Period balance',
                description: 'Summary of outflow, inflow and balance at the top of Transactions, the month or the search.',
            },
            dailyBalance: {
                title: 'Daily running balance',
                description: 'Available balance of your accounts (pocket reserves already deducted) at the end of each day of the statement, like a bank statement. Hidden while searching or filtering.',
            },
            dailyBalanceOpening: {
                title: 'Include previous balance',
                description: 'Starts from the balance carried over from the previous month. When off, the running total starts at zero on day 1.',
            },
            reservaMovements: {
                title: 'Pockets in the statement',
                description: "Shows pocket deposits and withdrawals in the account statement and in Transactions as informational rows. Totals don't change.",
            },
            pendingOnTop: {
                title: 'Pending highlighted',
                description: 'Keeps pending and projected items at the top of the list. When off, the list stays in chronological order by day.',
            },
            overdueInCurrentMonth: {
                title: 'Overdue in the current month',
                description: 'Pending items from past months show up at the top of the current month in Transactions and count in To pay and To receive, under Pending on Home.',
            },
        },
    },
    appIcon: {
        explainer: 'Make Kontto your own. Tap an icon to apply it.',
        itemAccessibility: '{{label}} icon',
        itemAccessibilityCurrent: '{{label}} icon (current)',
        applyError: {
            title: "Couldn't change the icon",
            description: 'Changing the icon only works in the installed app (not in Expo Go).',
        },
        applied: {
            androidTitle: 'Almost there!',
            androidDescription: 'Press Home to leave Kontto — the new icon appears on your home screen. When you reopen, you land back here.',
            iosTitle: 'Icon updated',
            iosDescription: 'Done! The new icon is already on your home screen.',
        },
        androidNote: 'On Android, the new icon appears when you close or minimize the app — and it may flicker or vanish for a moment on the home screen. That is normal system behavior.',
        confirmDialog: {
            title: 'Change the app icon?',
            message: 'To apply the {{label}} icon, leave Kontto (Home button) — the new icon appears on your home screen. When you reopen, you come straight back here.',
            confirm: 'Change',
            cancel: 'Not now',
        },
    },
    homeLayout: {
        explainer: 'Drag by the handle to reorder your home blocks, and tap the eye to show or hide. Month navigation and the balance always stay at the top.',
        // Appended to the explainer with a space (only without Pro).
        explainerProSuffix: 'Reordering and showing/hiding is a Kontto Pro feature.',
        configureShortcuts: 'Configure shortcuts',
        restoreDefault: 'Restore default',
    },
    // Customizable tab bar slot (ADR 0122).
    tabSlot: {
        explainer: 'Choose what the fourth button on the bar opens. Whatever leaves the bar goes to the More tab.',
        // Appended to the explainer with a space (only without Pro).
        explainerProSuffix: 'Swapping the shortcut is a Kontto Pro feature.',
        livePreview: 'Live preview',
        previewPill: 'Fourth button · {{label}}',
        defaultHint: 'default',
        save: 'Save shortcut',
        saved: 'Fourth button now opens {{label}}',
        quickTitle: 'Bar shortcut',
        quickSeeAll: 'See all',
        quickClose: 'Close',
        short: { cashFlow: 'Cash flow', goals: 'Goals', financings: 'Financing' },
    },
    analysisLayout: {
        explainer: 'Drag by the handle to reorder the Analysis tab blocks, and tap the eye to show or hide. Month navigation and the view selector always stay at the top.',
        // Appended to the explainer with a space (only without Pro).
        explainerProSuffix: 'Reordering, showing/hiding, and the extra charts are Kontto Pro features.',
        restoreDefault: 'Restore default',
        sections: {
            overview: 'Month summary',
            categories: 'Spending by category',
            monthlyFlow: 'Monthly flow',
            netWorth: 'Net worth',
            spendingPace: 'Spending pace',
            topExpenses: 'Biggest expenses',
            savingsRate: 'Savings rate',
            weekday: 'Spending by weekday',
            fixedVariable: 'Fixed and variable',
            reportsLink: 'Advanced reports',
        },
    },
    homeShortcuts: {
        explainer: 'Choose your home screen shortcuts. Drag by the handle to order them and tap the trash to remove.',
        // Appended to the explainer with a space (only without Pro).
        explainerProSuffix: 'Choosing and ordering shortcuts is a Kontto Pro feature.',
        empty: 'No shortcuts on home yet. Add some below.',
        addSectionTitle: 'Add',
        restoreDefault: 'Restore default',
    },
    // app/settings/ai.tsx — AI assistant (BYOK)
    ai: {
        explainer: 'Connect your own AI key — free in Kontto. It goes straight from your device to the provider; nothing passes through our servers.',
        providerLabel: 'Provider',
        providerOptions: {
            gemini: { description: 'Google · has a free tier' },
            openai: { description: 'Requires active credits' },
            openrouter: { description: 'Many models, one key' },
        },
        apiKeyLabel: 'API key',
        dataSharingLabel: 'Data sharing',
        helpLink: {
            gemini: 'How to get your Gemini key?',
            openai: 'How to get your OpenAI key?',
            openrouter: 'How to get your OpenRouter key?',
        },
        openrouterModel: {
            label: 'Model (optional)',
            placeholder: 'Search models',
            hint: 'Tap the field to browse the catalog — free models come first. Left empty, Kontto uses a fast, inexpensive model.',
            free: 'Free',
            pricePerM: '${{price}}/M',
            catalogError: 'Couldn\'t load the model catalog. You can still type the ID manually.',
            retry: 'Try again',
        },
        connected: {
            title: 'AI active with {{provider}}',
            subtitle: 'Tap "Test connection" to check the key.',
            validated: 'Connection validated a moment ago.',
        },
        status: {
            validated: 'Connection validated successfully.',
            failed: 'Failed to test the connection.',
        },
        testButton: 'Test connection',
        testingButton: 'Testing...',
        saveButton: 'Save',
        savingButton: 'Saving...',
        keyNote: 'The key is stored on this device only.',
        legacyGeminiKey: {
            title: 'Your old key expires in September',
            description: 'Generate a new one in Google AI Studio ("AQ." format) and paste it here.',
            action: 'How to generate a new key',
        },
        removeKey: {
            action: 'Remove key',
            confirmTitle: 'Remove the key?',
            confirmMessage: 'AI stops working until you paste another one. The key is deleted from this device only — nothing changes in your provider account.',
            confirmLabel: 'Remove',
            toast: 'Key removed',
        },
        dataSharing: {
            description: 'You authorized sending data to the AI (the financial summary for analyses, or the statement descriptions you asked it to categorize). You can revoke it anytime — analyses go back to being generated locally and statements are categorized on the device only.',
            revoke: 'Revoke authorization',
        },
        toasts: {
            aiDisabled: 'AI disabled',
            saved: 'Settings saved',
            saveFailed: 'Failed to save',
            insertKeyFirst: 'Enter an API key first',
        },
    },
    // components/settings/ApiKeyHelpSheet.tsx — step by step per provider
    apiKeyHelp: {
        gemini: {
            title: 'How to get your Gemini key',
            intro: 'It takes about 2 minutes and the free tier is usually enough.',
            urlLabel: 'Open Google AI Studio',
            keyHint: 'New keys start with "AQ." and older ones with "AIza" — both work.',
            step1: 'Sign in to Google AI Studio with your Google account.',
            step2: 'Tap "Create API key".',
            step3: 'Choose a project — new or existing.',
            step4: 'Copy the key and paste it here in the field.',
        },
        openai: {
            title: 'How to get your OpenAI key',
            intro: 'The API requires active credits/billing on your account.',
            urlLabel: 'Open OpenAI Platform',
            keyHint: 'The key starts with "sk-".',
            step1: 'Sign in to your account at platform.openai.com.',
            step2: 'Under "API keys", tap "Create new secret key".',
            step3: 'Copy it right away — it is shown only once.',
            step4: 'Paste it here in the key field.',
        },
        openrouter: {
            title: 'How to get your OpenRouter key',
            intro: 'One key gives access to hundreds of models — some free, the rest with prepaid credits.',
            urlLabel: 'Open OpenRouter',
            keyHint: 'The key starts with "sk-or-".',
            step1: 'Create an account at openrouter.ai (Google sign-in works).',
            step2: 'Under "Keys", tap "Create Key".',
            step3: 'Copy the key and paste it here in the field.',
            step4: 'Optional: pick a model from the catalog in the "Model" field.',
        },
    },
    // app/settings/backup.tsx — main Backup screen (redesign "Backup - Final")
    backup: {
        // Strip for the build without Drive (S0) and the stopped-backup problem (S3) — ADR 0193.
        status: {
            unavailableTitle: 'Cloud backup',
            unavailableSubtitle: 'Available after updating the app (new build).',
            stoppedTitle: 'Backup stopped',
            reconnectAction: 'Reconnect',
            reconnecting: 'Reconnecting...',
        },
        errors: {
            driveNeedsUpdate: "Google Drive backup doesn't work in this version of the app. Update Kontto from the store to back up again.",
        },
        syncCard: {
            beta: 'Beta',
            syncing: 'Syncing...',
            reset: 'Reset sync',
            resetSub: 'If no one remembers the password used to turn it on',
            forgotRow: "I forgot the other device's password",
            mismatchTitle: "The password doesn't match the other device",
            mismatchBody: 'Sync was turned on on another device with a different protection password. Enter the same password used there.',
            mismatchRetry: 'Try again',
        },
        restorePoints: {
            title: 'Restore',
            checking: 'Checking...',
        },
        toasts: {
            connectFailed: "Couldn't connect",
            connected: 'Google Drive connected',
            sendFailed: 'Failed to send',
            noDestinationTitle: 'Choose a destination',
            noDestinationBody: 'Connect Google Drive or choose a folder on this device.',
            folderChosen: 'Folder chosen',
            folderChooseFailed: 'Failed to choose the folder',
            folderManualOnlyTitle: '{{folder}}: manual backup only',
            folderManualOnlyBody: 'That cloud does not accept automatic backups. Choose a folder on this device for the automatic one.',
            folderSaveFailed: 'Failed to save to the folder',
            folderShareTitle: 'Backup ready to save',
            folderShareBody: 'Pick {{folder}} in the share sheet to store the encrypted file.',
            wrongPassTitle: 'Incorrect password',
            wrongPassBody: 'Use the same Password protection password, identical on both devices.',
            syncEnabled: 'Sync enabled',
            syncReceived: "The other device's data has arrived.",
            syncResultBoth: "Your changes were sent and the other device's arrived.",
            syncResultSent: 'Your changes were sent.',
            syncResultReceived: 'Changes arrived from the other device.',
            syncResultNothing: 'Everything was already up to date.',
            syncFirstFailed: 'Enabled, but the first sync failed',
            syncResetDone: 'Sync reset',
            syncResetDoneBody: 'This device is the base now. On the others, open Kontto and enter this same protection password.',
            syncResetCycleFailed: 'Reset, but the first upload failed',
            syncResetFailed: "Couldn't reset",
            rejoinBackupFailed: "Couldn't save the restore point",
            rejoinMismatch: "That's not the password used on the other device",
            enableFailed: 'Failed to enable',
            joinFailed: 'Failed to join the space',
            synced: 'Synced',
            syncFailed: 'Failed to sync',
            protectionRemoved: 'Protection removed',
            protectionRemovedAndroid: 'Backups go back to plain files. Automatic backup (folder and Drive) and sync were turned off — they require a password.',
            protectionRemovedIos: 'Backups go back to plain files. Automatic backup to Drive and sync were turned off — they require a password.',
            removeFailed: 'Failed to remove',
            encryptFailed: 'Failed to encrypt',
            encryptedReadyTitle: 'Encrypted backup ready',
            encryptedReadyBody: 'Password-protected and ready to share.',
            readyTitle: 'Backup ready',
            readyBody: 'Prepared for local sharing.',
            createFailed: 'Failed to create backup',
        },
        removeDialog: {
            title: 'Remove password protection',
            message: 'The next backups go back to plain files (JSON).\n\nBackups already created with a password still require that password to restore.',
            confirm: 'Remove',
        },
        changeAccountDialog: {
            title: 'Change Google account',
            message: 'Your backups stay in the current account — nothing is deleted.\n\nWe will disconnect and open Google sign-in so you can choose another account.',
            confirm: 'Change account',
        },
        joinDialog: {
            title: "Replace this device's data",
            message: "This Drive already has data synced from another device.\n\nTo join the sync, THIS device's data will be replaced by the other device's — they don't merge. If you want to keep what's here, export a backup first.\n\nNothing changes if you cancel.",
            confirm: 'Replace and sync',
        },
        // "Turn on" wizard (ADR 0193): Google, then the password, and backup + sync end up on.
        setup: {
            hero: {
                title: 'Keep your data safe in Drive',
                subtitle: 'Turn it on once. The rest happens on its own.',
                daily: 'A copy every day, without you remembering',
                devices: 'The same data on your phone and tablet',
                locked: 'Locked with a password only you know',
                cta: 'Turn on',
                proNote: 'A Kontto Pro feature. Restoring stays free.',
            },
            step: '{{current}} of {{total}}',
            stepA11y: 'Step {{current}} of {{total}}',
            google: {
                title: 'Sign in with your Google account',
                body: 'Your data stays in a Kontto folder in your Drive, locked. Neither Google nor Kontto can read it.',
                cta: 'Continue with Google',
                checking: 'Checking your Drive…',
                later: 'Not now',
            },
            create: {
                title: 'Create your protection password',
                titleNew: 'Create a new password',
                body: "You'll use this same password on your other devices. Without it, nobody can recover your data.",
                match: 'Passwords match',
                cta: 'Turn on',
            },
            found: {
                title: 'We found your data',
                body: 'Last copy {{when}}. Enter the protection password you use on your other device.',
                bodyNoDate: 'Enter the protection password you use on your other device.',
                wrong: "That's not it. Use the same password as your other device.",
                replaceNote: "What's on this device is replaced by what's in Drive.",
                cta: 'Bring my data',
                forgot: 'I forgot the password',
            },
            unlock: {
                title: 'Enter your protection password',
                body: 'The one you created on this device.',
                wrong: 'Wrong password.',
                cta: 'Turn on',
            },
            field: {
                password: 'Password',
                confirm: 'Confirm password',
                show: 'Show password',
                hide: 'Hide password',
            },
            working: {
                title: 'Turning on protection',
                body: 'This takes a few seconds. Keep the app open.',
                pass: 'Password created',
                sync: 'Turning on sync',
                firstCopy: 'First copy in Drive',
            },
            done: {
                title: 'All set',
                body: 'Your data is in Drive and will stay the same across your devices.',
            },
        },
        // "All protected" panel and the screen's list (ADR 0193).
        panel: {
            okTitle: 'All protected',
            okBoth: 'Backup and sync are on',
            okBackupOnly: 'Backup on · sync off',
            okSyncOnly: 'Sync on · backup off',
            okFolderOnly: 'Backing up to a device folder',
            lastCopy: 'Last copy',
            synced: 'Synced',
            notYet: 'not yet',
            today: 'today, {{time}}',
            yesterday: 'yesterday, {{time}}',
            saveNow: 'Save now',
            saving: 'Saving…',
            saved: 'Saved',
            offTitle: 'Protection is off',
            offSubtitle: 'Nothing was deleted',
            offCta: 'Turn back on',
            pausedTitle: 'Sync paused',
            pausedSubtitle: 'It was reset on another device',
            pausedBody: "Enter the password used there to get back in. We save a copy of this device's data first.",
            pausedCta: 'Enter password',
            pausedUseAsBase: 'Use this device as the base',
            failingTitle: 'Sync failed',
            failingCta: 'Try again',
            needsKeyBody: 'This device needs your protection password to sync again.',
            tipTitle: 'Next, on your other device',
            tipBody: 'Open Kontto, tap Turn on, sign in with {{email}} and use this same password.',
            tipDismissA11y: 'Dismiss tip',
            rowOtherDevice: 'Use on another device',
            rowOtherDeviceSub: 'Same Google account and password',
            rowAccount: 'Google account',
            rowPassword: 'Protection password',
            rowPasswordSub: 'Asked on each new device',
            rowPasswordRemove: 'Remove protection password',
            rowMore: 'More options',
            rowMoreSubOff: 'Device folder, export file',
            rowMoreSub: 'Sync, folder, export',
            restoreFallback: 'From a file or a saved point',
            restoreCount_one: '{{count}} saved point',
            restoreCount_other: '{{count}} saved points',
        },
        otherDevice: {
            title: 'Use on another device',
            body: 'Takes a minute. Your data arrives on its own.',
            step1: 'Open Kontto on the other device',
            step1Sub: 'Settings › Backup & Sync',
            step2: 'Tap Turn on',
            step2Sub: 'Sign in with {{email}}',
            step3: 'Enter the same protection password',
            step3Sub: 'The one used on this device',
            cta: 'Got it',
        },
        // "More options" route (app/settings/backup-more.tsx).
        more: {
            sectionSync: 'Sync',
            syncSwitch: 'Sync devices',
            syncOn: 'On · {{when}}',
            syncOff: 'Off',
            sectionCopies: 'Other copies',
            folder: 'Also save to a device folder',
            folderNone: 'No folder chosen',
            folderManualOnly: '{{folder}} only keeps manual backups. For the automatic one, choose a folder on this device.',
            export: 'Export file',
            exporting: 'Exporting…',
            exportSub: 'A locked copy to keep wherever you like',
            sectionOff: 'Turn off',
            turnOff: 'Turn off backup and sync',
            turnOffSub: 'Nothing is deleted from Drive or this device',
            turnOffDialog: {
                title: 'Turn off protection?',
                message: "Daily backup and sync stop on this device. Nothing is deleted from Drive or here. To turn it back on, you'll enter your password.",
                confirm: 'Turn off',
            },
        },
        resetSyncSheet: {
            title: 'Reset sync?',
            subtitle: 'Use this when no one remembers the password used to turn on sync.',
            baseTitle: 'This device becomes the base',
            baseBody: 'Its data becomes the data on every device.',
            keepTitle: 'Nothing is deleted here',
            keepBody: 'Not even the restore points in Drive.',
            othersTitle: "Other devices will ask for this device's password",
            othersBody: 'When they rejoin, they get this data. Each one saves a restore point first.',
            warning: 'Do this on the device with the most complete data, and update Kontto on the others first.',
            cancel: 'Cancel',
            confirm: 'Continue',
            footnote: "Next, we'll ask for this device's protection password.",
            footnoteNew: "Next, you'll create a new password.",
        },
        rejoinSyncDialog: {
            title: "Receive the other device's data",
            message: "Sync was reset on another device. To come back, THIS device's data is replaced by the data there.\n\nFirst, a restore point with the data from here is saved to Google Drive.\n\nIf this device has the most complete data, cancel and tap \"Reset sync\" here.",
            confirm: 'Receive the data',
        },
    },
    // app/settings/backups.tsx — Restore points (2b)
    backups: {
        warnNote: 'Restoring replaces the entire current local database.',
        driveSection: 'In your Google account',
        folderSection: 'On this device',
        loadingVersions: 'Loading versions...',
        driveEmpty: 'No restore points yet.',
        folderEmpty: 'No versions in the folder.',
        rotationNote: 'We keep the {{keepVersions}} most recent points. When a new one is created, the oldest drops off automatically.',
        emptyState: 'No backups saved yet. You can restore from a saved file.',
        restoreFileA11y: 'Restore from another file',
        restoring: 'Restoring...',
        restoreFromFile: 'Restore from another file…',
        sourceDrive: 'Drive',
        sourceFolder: 'Folder',
        rowEncrypted: '{{source}} · Encrypted',
        latestBadge: 'LATEST',
        restore: 'Restore',
        deletePointA11y: 'Delete point from {{date}}',
        progress: {
            title: 'Restoring your backup',
            doneTitle: 'Backup restored',
            doneSubtitle: 'All set!',
            stepDownload: 'Downloading from your Drive...',
            stepRead: 'Reading the file...',
            stepDecrypt: 'Decrypting your data...',
            stepApply: 'Bringing back your records...',
            stepFinish: 'Tidying everything up...',
            note: 'This only takes a moment. Don’t close the app.',
            joinTitle: 'Turning on sync',
            joinDoneTitle: 'Devices in sync',
            joinDoneSubtitle: 'All set!',
            joinStepPrepare: 'Preparing this device...',
            joinStepDownload: 'Downloading synced data...',
        },
        toasts: {
            restoredTitle: 'Backup restored',
            restoredSyncKeep: 'Sent to your other devices. PIN and biometrics stay on this one.',
            restoredSyncPending: 'PIN and biometrics stay on this device. Sync will finish on the next cycle.',
            restoredLocal: 'PIN, biometrics and permissions stay on this device.',
            restoreFailed: 'Failed to restore',
            pointDeleted: 'Restore point deleted',
            deleteFailed: 'Failed to delete',
        },
        confirm: {
            file: 'Choose a backup file. This will replace the entire current local database. This action cannot be undone.',
            named: 'We will bring back the backup from {{date}}.\n\nThis will replace the entire current local database. This action cannot be undone.',
            syncWarning: '⚠️ Sync is on: this state will replace the data on YOUR OTHER devices too.',
            title: 'Restore backup?',
            confirmLabel: 'Replace now',
        },
        deleteDialog: {
            title: 'Delete restore point?',
            message: 'The point from {{date}} will be deleted from your Drive.\n\nThis action cannot be undone.',
            confirm: 'Delete',
        },
    },
    // app/settings/maintenance.tsx — production actions only; the __DEV__ block stays literal.
    maintenance: {
        title: 'Maintenance',
        subtitle: 'One-off actions that do not delete your transactions.',
        reopenIntro: {
            label: 'Reopen intro',
            description: 'Shows the welcome message again, without deleting anything.',
            button: 'Open welcome message',
        },
        reopenFirstSteps: {
            label: 'Reopen first steps',
            description: 'Brings the "Let\'s get started" card back to the Home, even if you completed, hid or skipped steps. Nothing is deleted.',
            button: 'Reopen checklist',
            toast: 'Checklist unlocked. It comes back on the Home if any step is still pending.',
        },
        purge: {
            label: 'Purge deleted records',
            description: 'Permanently removes what is in the trash (no longer shown in the app). Anything still tied to a transaction you kept stays.',
            button: 'Purge records',
        },
        factory: {
            sectionTitle: 'Erase everything and start over',
            blockLabel: 'Reset the app to its installed state',
            note: "Erases EVERYTHING on this device: transactions, accounts, cards, pockets and goals, plus your PIN, AI key, preferences and the history of screens you've seen. The app returns to a freshly installed state and restarts at onboarding.\n\nYour subscription isn't canceled — Pro comes back on its own when you sync. This action cannot be undone.",
            deleting: 'Erasing...',
            button: 'Erase everything and start over',
        },
        toasts: {
            purged: 'Records removed',
            purgeFailed: 'Failed to clear',
            wiped: 'App reset',
            wipeFailed: 'Failed to erase data',
        },
        purgeDialog: {
            title: 'Purge deleted records',
            message: 'This permanently removes the records in the trash (transactions, accounts, categories, etc). An account or card still tied to a transaction you kept stays. This action cannot be undone.',
            confirm: 'Purge',
        },
        factoryDialog: {
            title: 'Erase everything and start over',
            message: "This erases EVERYTHING on this device — transactions, accounts, cards, pockets, goals, PIN, AI key, preferences and the history of screens you've seen — and restarts the app at onboarding, as if freshly installed. Your subscription is not canceled. This can't be undone.",
            confirm: 'Erase everything',
        },
    },
    // components/settings/PassphraseDialog.tsx — create/enter the backup password
    passphraseDialog: {
        errorMinLength: 'Use at least {{min}} characters.',
        errorMismatch: 'The passwords do not match.',
        errorEmpty: 'Enter the backup password.',
        createTitle: 'Protect backup with a password',
        enterTitle: 'Backup password',
        syncTitle: 'Sync password',
        createSubtitle: 'Your backups will be encrypted with this password. Keep it safe: without it you cannot restore on another device.',
        enterSubtitle: 'Enter the password used to encrypt this backup.',
        syncSubtitle: 'It is the same backup protection password. It must match on every device.',
        busyCreateTitle: 'Protecting your backup',
        busyEnterTitle: 'Checking the password',
        busySyncTitle: 'Setting up sync',
        busyCreateSubtitle: 'Generating the encryption key. Takes a few seconds.',
        busyEnterSubtitle: 'Checking and opening the backup.',
        busySyncSubtitle: 'Generating the shared key. This takes a few seconds.',
        passPlaceholder: 'Password',
        confirmPlaceholder: 'Confirm password',
        hint: 'Minimum {{min}} characters.',
        cancel: 'Cancel',
        createSubmit: 'Enable',
        enterSubmit: 'Restore',
        syncSubmit: 'Turn on sync',
    },
    // components/settings/RuleFormSheet.tsx — categorization rule form
    ruleForm: {
        saveChanges: 'Save changes',
        create: 'Create rule',
        editTitle: 'Edit rule',
        newTitle: 'New rule',
        patternLabel: 'Text pattern',
        patternPlaceholder: 'e.g. PIX*IFOOD* | UBER*TRIP*',
        typeLabel: 'Rule type',
        typeExpense: 'Expense',
        typeIncome: 'Income',
        categoryLabel: 'Category',
        categorySelectedFallback: 'Selected category',
        selectCategoryExpense: 'Select expense category',
        selectCategoryIncome: 'Select income category',
        priorityLabel: 'Priority',
        ruleActive: 'Rule active',
    },
    // components/settings/LiveThemePreview.tsx — illustrative mini-Home for the theme carousel
    themePreview: {
        month: 'July 2026',
        totalBalance: 'Total balance',
        totalBalanceValue: 'R$ 12.480,55',
        income: 'Income',
        incomeValue: 'R$ 8.300,00',
        expense: 'Expenses',
        expenseValue: 'R$ 4.120,45',
        proBadge: 'PRO',
    },
    // app/settings/import.tsx — Import data (CSV/OFX/PDF/screenshots)
    import: {
        idle: {
            title: 'Import data',
            lead: 'Importing <0>adds to</0> what you already have — nothing is erased.',
            local: 'Everything happens on your device',
            subtitle: "Bring your history from another app or your bank. The import adds to what's already there — it deletes nothing.",
            chooseFile: 'Choose the file',
            csv: 'Import CSV spreadsheet',
            ofx: 'Import bank transactions (OFX)',
            pdf: 'Import bank transactions (PDF)',
            prints: 'Import transactions from screenshots (photo)',
            modelLink: 'See the CSV template and download the example →',
            importSection: 'Import',
        },
        sources: {
            recommended: 'RECOMMENDED',
            ofxTitle: 'OFX statement',
            ofxSubtitle: 'Export it in your bank app · most accurate',
            pdfTitle: 'PDF statement from your bank',
            pdfSubtitle: 'We read the transactions from the PDF',
            sheetTitle: 'CSV or Excel spreadsheet',
            sheetSubtitle: "Kontto's template or another app's export",
            printsTitle: 'Statement screenshots',
            printsSubtitle: 'Last resort — OCR can get it wrong',
        },
        restore: {
            title: 'Restore a Kontto backup',
            subtitle: 'Restoring REPLACES everything; importing adds to it.',
            label: '.json or .kontto file',
            button: 'Go to restore points',
        },
        export: {
            section: 'Export',
            busy: 'Preparing the file...',
            backupTitle: 'Full backup',
            backupSubtitle: 'Takes everything; Kontto only',
            sheetTitle: 'Transactions as a spreadsheet',
            sheetSubtitle: 'CSV spreadsheet or PDF report',
        },
        backupFileDialog: {
            title: 'This is a Kontto backup',
            message: 'A backup does not go through the import — it replaces the app data. Want to go to the restore screen?',
            confirm: 'Restore backup',
        },
        busy: {
            preparingPreview: 'Preparing the preview...',
            readingFile: 'Reading the file...',
            readingStatement: 'Reading the file...',
            readingPrints: 'Reading the screenshots...',
            importing_one: 'Importing {{count}} transaction...',
            importing_other: 'Importing {{count}} transactions...',
            processing: 'Processing...',
        },
        preview: {
            title: 'Import preview',
            heroLabel_one: 'transaction to import',
            heroLabel_other: 'transactions to import',
            incomeShort: 'income',
            expenseShort: 'expenses',
            transferShort: 'transfers',
            balanceFromFile: 'Balance came from the file · matches the bank',
            accountsRow_one: '{{count}} new account: {{names}}',
            accountsRow_other: '{{count}} new accounts: {{names}}',
            accountsZeroed: 'Start at zero · adjust below',
            accountsBalance: 'Balance from the file applied · adjust below',
            taxonomyRow: '{{categories}} categories · {{subcategories}} new subcategories',
            taxonomyCategoriesOnly_one: '{{count}} new category',
            taxonomyCategoriesOnly_other: '{{count}} new categories',
            duplicatesRow_one: '{{count}} duplicate skipped',
            duplicatesRow_other: '{{count}} duplicates skipped',
            duplicatesSubtitle: 'Already in the app',
            toImportChip: '{{count}} to import',
            summaryLabel: 'Summary',
            toImport: 'Transactions to import',
            income: 'Income',
            expense: 'Expenses',
            transfer: 'Transfers',
            newAccounts: 'New accounts',
            newCategories: 'New categories',
            newSubcategories: 'New subcategories',
            duplicates: 'Duplicates (skipped)',
            warnings: 'Warnings',
            errors: 'Rows with errors',
        },
        balances: {
            title: 'New accounts — balance',
            subtitle: 'The file brings only the transactions, without a starting balance. By default new accounts start at zero (R$ 0,00).',
            zeroLabel: 'Zero the new accounts balance',
            zeroOn: 'All start at R$ 0,00. Turn off to enter the current balance of each account.',
            zeroOff: 'Enter the current balance of each account (or zero it) — the app adjusts the starting balance to match.',
            balancePlaceholder: 'Current balance',
            zero: 'Zero',
        },
        issues: {
            title: 'Warnings and errors',
            summaryWarnings_one: '{{count}} warning',
            summaryWarnings_other: '{{count}} warnings',
            summaryErrors_one: '{{count}} row with an error',
            summaryErrors_other: '{{count}} rows with errors',
            errorNote: 'Rows with errors are left out',
            noErrorsNote: 'Check before importing',
            expand: 'See details',
            collapse: 'Hide details',
            detailsLabel: 'Details (first 8)',
            linePrefix: 'Row {{line}}: ',
        },
        statementWarnings: {
            allIncome: "No transaction came through as money out. Some banks don't mark the expense sign in the PDF — check the types in the preview before importing.",
            incomplete: "The statement's balance column doesn't add up with the transactions we read. Some may be missing — check before importing.",
            singleAccount: "The spreadsheet doesn't say which account each transaction belongs to — everything goes to the account you pick. The total balance is right; the per-account split is lost.",
            unknownYear: "We couldn't confirm the year of these transactions from the spreadsheet. Check the dates in the preview before importing.",
            noDescription: "This Mercado Pago report doesn't say who you paid or who paid you: transactions come in named by type (Pix, QR payment…) and the yield is summed per month. For the names, import the account statement PDF instead.",
        },
        actions: {
            importCount: 'Import {{count}}',
            importing: 'Importing...',
            cancel: 'Cancel',
        },
        done: {
            title: 'Import complete',
            subtitle: 'Everything was added to what you already had.',
            viewTransactions: 'See imported transactions',
            importAnotherShort: 'Import another',
            accountsCreatedNamed: 'Account created: {{names}}',
            accountsCreatedNamed_other: 'Accounts created: {{names}}',
            subcategoriesSuffix_one: '+ {{count}} subcategory',
            subcategoriesSuffix_other: '+ {{count}} subcategories',
            leftOut: '{{duplicates}} duplicates and {{errors}} rows with errors were left out.',
            resultLabel: 'Result',
            imported: 'Transactions imported',
            accountsCreated: 'Accounts created',
            categoriesCreated: 'Categories created',
            subcategoriesCreated: 'Subcategories created',
            importAnother: 'Import another file',
            backHome: 'Back to home',
        },
        accountModal: {
            title: 'Which account is this from?',
            fileMeta_one: '{{count}} transaction · {{period}}',
            fileMeta_other: '{{count}} transactions · {{period}}',
            detected: 'Name detected in the file — confirm or change it',
            yourAccounts: 'Your accounts',
            description: 'If an account with this name already exists, the transactions go into it; otherwise, we create a new one.',
            placeholder: 'e.g. Chase',
            continue: 'Continue',
        },
        help: {
            title: 'How importing works',
            body: 'Importing adds to what you already have — it deletes nothing, unlike restoring a backup. Everything happens on your device.\n\nThere are two paths: a CSV in the Kontto template (download the example below and adjust yours) or the OFX file you export straight from your bank. You review a preview before confirming.',
            modelButton: 'See template and download example',
            formats: 'Best → worst accuracy: your bank\'s <0>OFX</0> and <1>PDF</1> are the most faithful (download straight from the bank app/site). CSV works with Kontto\'s template or other apps\'. <2>Screenshots</2> are a last resort — OCR gets names and amounts wrong; use only if you can\'t get OFX/PDF. You review a preview before confirming.',
        },
        toasts: {
            unrecognizedFileTitle: "We didn't recognize the file",
            legacyXlsTitle: 'Old spreadsheet format',
            legacyXlsBody: "This is an old .xls file we can't open. Open it in Excel or Google Sheets and save it as .xlsx or CSV.",
            unrecognizedSheetTitle: "We didn't recognize this spreadsheet",
            unrecognizedSheetBody: "We opened the spreadsheet but couldn't find a date and an amount. If it has a table, put the header row (Date, Amount, Description…) first; or export as CSV.",
            unrecognizedFileBody: 'The Date and Amount columns are missing. Check the CSV template and download the example from the on-screen link.',
            nothingToImport: 'Nothing to import',
            previewFailed: 'Failed to build the preview',
            readFileFailed: 'Failed to read the file',
            notOfx: 'File is not an OFX',
            ofxEmpty: 'OFX has no transactions',
            readStatementFailed: 'Failed to read the file',
            unrecognizedStatementTitle: "We didn't recognize the file",
            unrecognizedPdfBody: "We couldn't find any transactions in this PDF. If it's a card statement, use the card screen; for an account, try OFX.",
            unrecognizedPrintsBody: "We couldn't find any transactions in these screenshots. Whenever possible, prefer your bank's PDF/OFX — screenshots are less accurate.",
            pdfPasswordProtected: 'Password-protected PDF',
            readPrintsFailed: 'Failed to read the screenshots',
            importDoneTitle: 'Import complete',
            importDoneBody: '{{transactions}} transactions, {{accounts}} accounts and {{categories}} categories.',
            importFailed: 'Failed to import',
        },
    },
    // app/settings/auto-categorization.tsx — Automatic categorization
    autoCategorization: {
        empty: {
            title: 'No rules',
            subtitle: 'Create rules to categorize transactions automatically.',
            action: 'Create rule',
        },
        tester: {
            typeLabel: 'Type',
            typeExpense: 'Expense',
            typeIncome: 'Income',
            testLabel: 'Test',
            testPlaceholder: 'Type a description...',
            hint: 'Ignores accents and punctuation. Use `*` as a wildcard and `|` for alternatives.',
            rulePrefix: 'Rule: {{pattern}}',
            noMatch: 'No rule matched',
        },
        history: {
            title: 'Old history',
            scopeLabel: 'Scope',
            scopeAll: 'All',
            scopeExpense: 'Expense',
            scopeIncome: 'Income',
            showPreview: 'Preview the history',
            previewNote: 'Calculated only when you ask — it scans your old transactions, so it stays out of the screen load.',
            calculating: 'Calculating the history preview...',
            tileMatched: 'Matched',
            tileFillable: 'Fillable',
            tileOverwrite: 'Would change',
            noChanges: 'No old transaction needs to change with the current rules.',
            refresh: 'Refresh preview',
            applyFillable: 'Apply to uncategorized',
            overwrite: 'Overwrite mismatches',
            noCategory: 'No category',
        },
        rulesCount: 'Rules ({{count}})',
        ruleType: {
            income: 'Income',
            expense: 'Expense',
        },
        deleteDialog: {
            title: 'Delete rule',
            message: "Are you sure you want to delete this rule? This action can't be undone.",
            confirm: 'Delete',
        },
        reapplyDialog: {
            overwriteTitle: 'Overwrite history',
            safeTitle: 'Apply to old history',
            overwriteMessage: 'This updates {{count}} old transactions that currently have a category different from the suggested rule.',
            safeMessage: 'This fills in {{count}} old transactions with no category or an incomplete subcategory.',
            overwriteConfirm: 'Overwrite',
            safeConfirm: 'Apply',
        },
    },
    // app/settings/reminders.tsx — Reminders
    reminders: {
        permissionCard: {
            activeTitle: 'Notifications on',
            activeSubtitle: 'Alerts arrive normally on this device.',
            attentionTitle: 'Notifications not allowed yet',
            attentionSubtitle: 'Without the system permission, no reminder shows up on this device. Your choices here are kept.',
            attentionAction: 'Turn on notifications',
            blockedTitle: 'No reminder will arrive',
            blockedBadge: 'BLOCKED',
            blockedSubtitle: "Kontto's notifications are turned off in the system. Your choices are kept and take effect again once you allow them.",
            blockedAction: 'Open system settings',
            recoveryPathAndroid: 'Android › Apps › Kontto › Notifications',
            recoveryPathIos: 'Settings › Kontto › Notifications',
            pausedBadge: 'PAUSED',
        },
        groups: {
            due: 'Due dates',
            comeback: 'Come back',
            backup: 'Data safety',
        },
        backupAlert: {
            title: 'Warn me if there is no backup',
            subtitle: 'A notice when your entries go a long time without any copy.',
        },
        due: {
            title: 'Daily alerts',
            subtitle: 'A summary of what is due today — bills, invoices and recurring items.',
            subtitlePaused: 'Your choices are kept and take effect again once you allow them.',
        },
        slotsShort: {
            morning: 'Morning',
            evening: 'Evening',
        },
        slots: {
            morning: 'Morning reminder',
            evening: 'Evening reminder',
        },
        time: 'Time',
        inactivity: {
            title: 'Call me back',
            subtitle: 'If you go days without opening Kontto.',
            notifyAfter: 'Notify after',
            daysOption: '{{count}} days',
        },
        restoreDefault: 'Restore default',
    },
    // app/settings/feedback.tsx — Send feedback
    feedback: {
        heroTitle: 'Your feedback shapes Kontto',
        heroSubtitle: 'Suggestions, questions or something that did not work — the app is built by a single person, and every message is read and answered.',
        emailLabel: 'Contact email',
        copy: 'COPY',
        writeEmail: 'Write an email',
        opening: 'Opening...',
        versionNote: 'The app version already goes in the email. If you can, also mention your device — it helps us investigate.',
        emailSubject: 'Kontto feedback',
        supportIdLabel: 'Support code (tap to copy)',
        supportIdLine: 'Support code: {{id}}',
        toasts: {
            copied: 'Email copied',
            copyFailed: "Couldn't copy",
            noMailApp: 'No email app',
            supportIdCopied: 'Support code copied',
        },
    },
} satisfies DeepPartialStrings<TranslationSchema['settings']>;
